import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, Trash2 } from 'lucide-react';
import retiroCopiasService from '../../services/retiroCopiasService';
import { formatDate } from '../../utils';
import type { RetiroCopia } from '../../types';

interface DeleteRetiroConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  retiro: RetiroCopia;
  otroTramiteId: string;
}

export const DeleteRetiroConfirmDialog: React.FC<DeleteRetiroConfirmDialogProps> = ({
  open,
  onOpenChange,
  retiro,
  otroTramiteId,
}) => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => retiroCopiasService.deleteRetiro(retiro.id),
    onSuccess: () => {
      // Invalidar ambas queries para refrescar los datos
      queryClient.invalidateQueries({ queryKey: ['retiros', otroTramiteId] });
      queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
      onOpenChange(false);
      // TODO: Mostrar toast de éxito
    },
    onError: (error: any) => {
      // TODO: Mostrar toast de error
      console.error('Error al eliminar retiro:', error);
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Eliminar Retiro
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer. ¿Está seguro de que desea eliminar este retiro?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Warning banner */}
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium mb-1">Esta operación es irreversible</p>
                <p>Se eliminarán todos los datos del retiro, incluidos los documentos y firmas.</p>
              </div>
            </div>
          </div>

          {/* Retiro details */}
          <div className="space-y-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium">Retiro ID</p>
              <p className="text-sm font-medium text-gray-900">#{retiro.id}</p>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase font-medium">Fecha de Retiro</p>
              <p className="text-sm text-gray-900">{formatDate(retiro.fechaRetiro)}</p>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase font-medium">Copias Retiradas</p>
              <p className="text-sm text-gray-900">
                {retiro.cantidadTotalRetirada} {retiro.cantidadTotalRetirada === 1 ? 'copia' : 'copias'}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500 uppercase font-medium">Personas</p>
              <ul className="mt-1 space-y-1">
                {retiro.retiradores.map((retirador) => (
                  <li key={retirador.id} className="text-sm text-gray-900">
                    • {retirador.nombreCompleto} ({retirador.cantidadCopias} {retirador.cantidadCopias === 1 ? 'copia' : 'copias'})
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {deleteMutation.isError && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-800">
                Error al eliminar el retiro. Por favor, intente nuevamente.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar Retiro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
